import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { LocationViewModel } from "../../../../shared/models/locations/LocationViewModel.model";
import { LocationDailyScheduleViewModel } from "../../../../shared/models/locations/LocationDailyScheduleViewModel.model";
import { LocationEventViewModel, LocationEventType } from "../../../../shared/models/locations/LocationEventViewModel.model";
import { LocationsService } from "../../../shared/locations.service";
import { ToastrService } from "ngx-toastr";
import { ConfirmationService } from "primeng/api";

interface DayOfWeekOption {
  label: string;
  value: number;
}

interface DateColorMap {
  [dateKey: string]: string;
}

@Component({
  selector: "app-location-schedule-tab",
  templateUrl: "./location-schedule-tab.component.html",
  styleUrls: ["./location-schedule-tab.component.css"],
})
export class LocationScheduleTabComponent implements OnInit, OnChanges {
  @Input() location: LocationViewModel;
  @Input() isEditMode: boolean;

  @Output() hasChanges = new EventEmitter<boolean>();

  dailySchedules: LocationDailyScheduleViewModel[] = [];
  displayDialog: boolean = false;
  newScheduleRecord: LocationDailyScheduleViewModel = this.getEmptyScheduleRecord();
  isEditingRecord: boolean = false;
  isLoadingSchedules: boolean = false;

  locationEvents: LocationEventViewModel[] = [];
  displayEventDialog: boolean = false;
  newEventRecord: LocationEventViewModel = this.getEmptyEventRecord();
  isEditingEvent: boolean = false;
  isLoadingEvents: boolean = false;

  // Calendar properties
  selectedDate: Date = new Date();
  dateColorMap: DateColorMap = {};
  private dateEventTypeMap: { [dateKey: string]: string[] } = {};
  private readonly lockedCalendarDate: Date = new Date();

  dayOfWeekOptions: DayOfWeekOption[] = [
    { label: "Sunday", value: 0 },
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
  ];

  constructor(
    private locationService: LocationsService,
    private toastr: ToastrService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    if (this.location && this.location.id) {
      this.loadDailySchedules();
      this.loadLocationEvents();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["location"] && !changes["location"].firstChange) {
      if (this.location && this.location.id) {
        this.loadDailySchedules();
        this.loadLocationEvents();
      }
    }
  }

  private loadDailySchedules(): void {
    if (!this.location || !this.location.id || this.isLoadingSchedules) {
      return;
    }

    this.isLoadingSchedules = true;
    this.locationService.getLocationDailySchedules(this.location.id).subscribe({
      next: (schedules: LocationDailyScheduleViewModel[]) => {
        this.dailySchedules = schedules || [];
        this.isLoadingSchedules = false;
      },
      error: (error) => {
        console.error("Error loading daily schedules:", error);
        this.toastr.error("Failed to load daily schedules", "Error");
        this.dailySchedules = [];
        this.isLoadingSchedules = false;
      },
    });
  }

  private getEmptyScheduleRecord(): LocationDailyScheduleViewModel {
    return {
      locationId: "",
      dayOfWeek: 0,
      open: "08:00",
      close: "17:00",
      closedAllDay: false,
      notes: "",
    };
  }

  public showAddDialog(): void {
    if (!this.isEditMode) {
      return;
    }
    this.isEditingRecord = false;
    this.newScheduleRecord = this.getEmptyScheduleRecord();
    if (this.location && this.location.id) {
      this.newScheduleRecord.locationId = this.location.id;
    }
    this.displayDialog = true;
  }

  public editScheduleRecord(record: LocationDailyScheduleViewModel): void {
    if (!this.isEditMode) {
      return;
    }
    this.isEditingRecord = true;
    this.newScheduleRecord = { ...record };
    this.displayDialog = true;
  }

  public deleteScheduleRecord(record: LocationDailyScheduleViewModel): void {
    if (!this.isEditMode) {
      return;
    }

    const dayName = this.getDayOfWeekName(record.dayOfWeek);
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the schedule for ${dayName}?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.locationService.deleteLocationDailySchedule(record.id!).subscribe({
          next: () => {
            const index = this.dailySchedules.findIndex((r) => r.id === record.id);
            if (index > -1) {
              this.dailySchedules.splice(index, 1);
              this.toastr.success("Schedule deleted successfully", "Success");
              this.onFieldChange();
            }
          },
          error: (error) => {
            console.error("Error deleting schedule:", error);
            this.toastr.error("Failed to delete schedule", "Error");
          },
        });
      },
    });
  }

  public saveScheduleRecord(): void {
    // Validate required fields
    if (this.newScheduleRecord.dayOfWeek === null || this.newScheduleRecord.dayOfWeek === undefined) {
      this.toastr.warning("Day of Week is required", "Validation Error");
      return;
    }

    if (!this.newScheduleRecord.closedAllDay) {
      if (!this.newScheduleRecord.open || this.newScheduleRecord.open.trim() === "") {
        this.toastr.warning("Open time is required when not closed all day", "Validation Error");
        return;
      }
      if (!this.newScheduleRecord.close || this.newScheduleRecord.close.trim() === "") {
        this.toastr.warning("Close time is required when not closed all day", "Validation Error");
        return;
      }

      // Validate time format (HH:mm)
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(this.newScheduleRecord.open)) {
        this.toastr.warning("Open time must be in HH:mm format", "Validation Error");
        return;
      }
      if (!timeRegex.test(this.newScheduleRecord.close)) {
        this.toastr.warning("Close time must be in HH:mm format", "Validation Error");
        return;
      }
    }

    // Check for duplicate day of week
    const existingIndex = this.dailySchedules.findIndex(
      (s) => s.dayOfWeek === this.newScheduleRecord.dayOfWeek && s.id !== this.newScheduleRecord.id
    );
    if (existingIndex > -1) {
      this.toastr.warning("A schedule already exists for this day of week", "Validation Error");
      return;
    }

    if (this.isEditingRecord) {
      // Update existing record
      this.locationService.updateLocationDailySchedule(this.newScheduleRecord).subscribe({
        next: (updatedSchedule: LocationDailyScheduleViewModel) => {
          const index = this.dailySchedules.findIndex((r) => r.id === this.newScheduleRecord.id);
          if (index > -1) {
            this.dailySchedules[index] = updatedSchedule;
          }
          this.toastr.success("Schedule updated successfully", "Success");
          this.onFieldChange();
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error updating schedule:", error);
          this.toastr.error("Failed to update schedule", "Error");
        },
      });
    } else {
      // Create new record
      this.locationService.createLocationDailySchedule(this.newScheduleRecord).subscribe({
        next: (newSchedule: LocationDailyScheduleViewModel) => {
          this.dailySchedules.push(newSchedule);
          this.toastr.success("Schedule created successfully", "Success");
          this.onFieldChange();
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error creating schedule:", error);
          this.toastr.error("Failed to create schedule", "Error");
        },
      });
    }
  }

  public closeDialog(): void {
    this.displayDialog = false;
    this.newScheduleRecord = this.getEmptyScheduleRecord();
  }

  public onDialogShow(): void {
    // Remove focus from any auto-focused input when dialog opens
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.tagName === "INPUT") {
        activeElement.blur();
      }
    }, 0);
  }

  public getDayOfWeekName(dayOfWeek: number): string {
    const option = this.dayOfWeekOptions.find((opt) => opt.value === dayOfWeek);
    return option ? option.label : "Unknown";
  }

  public onClosedAllDayChange(): void {
    if (this.newScheduleRecord.closedAllDay) {
      // Clear times when closed all day
      this.newScheduleRecord.open = "";
      this.newScheduleRecord.close = "";
    } else {
      // Set default times if empty
      if (!this.newScheduleRecord.open) {
        this.newScheduleRecord.open = "08:00";
      }
      if (!this.newScheduleRecord.close) {
        this.newScheduleRecord.close = "17:00";
      }
    }
  }

  onFieldChange() {
    this.hasChanges.emit(true);
  }

  private loadLocationEvents(): void {
    if (!this.location || !this.location.id || this.isLoadingEvents) {
      return;
    }

    this.isLoadingEvents = true;
    this.locationService.getLocationEvents(this.location.id).subscribe({
      next: (events: LocationEventViewModel[]) => {
        this.locationEvents = (events || []).map((event) => ({
          ...event,
          startDate: new Date(event.startDate),
          endDate: new Date(event.endDate),
          locationEventType: event.locationEventType as LocationEventType,
        }));
        this.buildDateColorMap();
        this.isLoadingEvents = false;
      },
      error: (error) => {
        console.error("Error loading location events:", error);
        this.toastr.error("Failed to load location events", "Error");
        this.locationEvents = [];
        this.buildDateColorMap();
        this.isLoadingEvents = false;
      },
    });
  }

  private getEmptyEventRecord(): LocationEventViewModel {
    return {
      locationId: "",
      startDate: new Date(),
      endDate: new Date(),
      locationEventType: LocationEventType.Outage,
      notes: "",
    };
  }

  public showAddEventDialog(): void {
    if (!this.isEditMode) {
      return;
    }
    this.isEditingEvent = false;
    this.newEventRecord = this.getEmptyEventRecord();
    if (this.location && this.location.id) {
      this.newEventRecord.locationId = this.location.id;
    }
    this.displayEventDialog = true;
  }

  public editEventRecord(record: LocationEventViewModel): void {
    if (!this.isEditMode) {
      return;
    }
    this.isEditingEvent = true;
    this.newEventRecord = {
      ...record,
      startDate: new Date(record.startDate),
      endDate: new Date(record.endDate),
    };
    this.displayEventDialog = true;
  }

  public deleteEventRecord(record: LocationEventViewModel): void {
    if (!this.isEditMode) {
      return;
    }

    const eventTypeName = this.getEventTypeName(record.locationEventType);
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this ${eventTypeName} event?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.locationService.deleteLocationEvent(record.id!).subscribe({
          next: () => {
          const index = this.locationEvents.findIndex((r) => r.id === record.id);
          if (index > -1) {
            this.locationEvents.splice(index, 1);
            this.buildDateColorMap(); // Update calendar colors
            this.toastr.success("Event deleted successfully", "Success");
            this.onFieldChange();
          }
          },
          error: (error) => {
            console.error("Error deleting event:", error);
            this.toastr.error("Failed to delete event", "Error");
          },
        });
      },
    });
  }

  public saveEventRecord(): void {
    // Validate required fields
    if (!this.newEventRecord.startDate) {
      this.toastr.warning("Start Date is required", "Validation Error");
      return;
    }

    if (!this.newEventRecord.endDate) {
      this.toastr.warning("End Date is required", "Validation Error");
      return;
    }

    if (this.newEventRecord.endDate < this.newEventRecord.startDate) {
      this.toastr.warning("End Date must be greater than or equal to Start Date", "Validation Error");
      return;
    }

    if (this.newEventRecord.locationEventType === null || this.newEventRecord.locationEventType === undefined) {
      this.toastr.warning("Event Type is required", "Validation Error");
      return;
    }

    // Convert Date to DateOnly format (YYYY-MM-DD) by creating a new Date with only date components
    const formatDateOnly = (date: Date): Date => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(year, month, day);
    };

    const eventData = {
      ...this.newEventRecord,
      startDate: formatDateOnly(new Date(this.newEventRecord.startDate)),
      endDate: formatDateOnly(new Date(this.newEventRecord.endDate)),
    };

    if (this.isEditingEvent) {
      // Update existing record
      this.locationService.updateLocationEvent(eventData).subscribe({
        next: (updatedEvent: LocationEventViewModel) => {
          const index = this.locationEvents.findIndex((r) => r.id === this.newEventRecord.id);
          if (index > -1) {
            this.locationEvents[index] = {
              ...updatedEvent,
              startDate: new Date(updatedEvent.startDate),
              endDate: new Date(updatedEvent.endDate),
              locationEventType: updatedEvent.locationEventType as LocationEventType,
            };
          }
          this.buildDateColorMap(); // Update calendar colors
          this.toastr.success("Event updated successfully", "Success");
          this.onFieldChange();
          this.closeEventDialog();
        },
        error: (error) => {
          console.error("Error updating event:", error);
          this.toastr.error("Failed to update event", "Error");
        },
      });
    } else {
      // Create new record
      this.locationService.createLocationEvent(eventData).subscribe({
        next: (newEvent: LocationEventViewModel) => {
          this.locationEvents.push({
            ...newEvent,
            startDate: new Date(newEvent.startDate),
            endDate: new Date(newEvent.endDate),
            locationEventType: newEvent.locationEventType as LocationEventType,
          });
          this.buildDateColorMap(); // Update calendar colors
          this.toastr.success("Event created successfully", "Success");
          this.onFieldChange();
          this.closeEventDialog();
        },
        error: (error) => {
          console.error("Error creating event:", error);
          this.toastr.error("Failed to create event", "Error");
        },
      });
    }
  }

  public closeEventDialog(): void {
    this.displayEventDialog = false;
    this.newEventRecord = this.getEmptyEventRecord();
  }

  public onEventDialogShow(): void {
    // Remove focus from any auto-focused input when dialog opens
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.tagName === "INPUT") {
        activeElement.blur();
      }
    }, 0);
  }

  public getEventTypeName(eventType: LocationEventType | number | undefined): string {
    if (eventType === undefined || eventType === null) {
      return "Unknown";
    }
    const typeValue = typeof eventType === 'number' ? eventType : (eventType as number);
    switch (typeValue) {
      case LocationEventType.Outage:
      case 0:
        return "Outage";
      case LocationEventType.Closed:
      case 1:
        return "Closed";
      default:
        console.warn('Unknown event type value:', typeValue);
        return "Unknown";
    }
  }

  public getEventTypeOptions(): { label: string; value: LocationEventType }[] {
    return [
      { label: "Outage", value: LocationEventType.Outage },
      { label: "Closed", value: LocationEventType.Closed },
    ];
  }

  private buildDateColorMap(): void {
    // Clear existing map
    this.dateColorMap = {};
    this.dateEventTypeMap = {};
    
    // Map to store colors for location events
    const eventTypeColors: { [key: string]: string } = {
      'Outage': "#F44336", // Red
      'Closed': "#9E9E9E", // Gray
    };

    // Process each location event
    this.locationEvents.forEach((event) => {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      const eventTypeName = this.getEventTypeName(event.locationEventType);
      
      // Mark all dates in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateKey = this.getDateKey(currentDate);
        // Use the event type color, defaulting to gray if not found
        this.dateColorMap[dateKey] = eventTypeColors[eventTypeName] || "#9E9E9E";
        const typesForDate = this.dateEventTypeMap[dateKey] ?? [];
        if (!typesForDate.includes(eventTypeName)) {
          typesForDate.push(eventTypeName);
        }
        this.dateEventTypeMap[dateKey] = typesForDate;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });
  }

  private getDateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  public getDateStyle(dateObj: any): any {
    // PrimeNG passes a date object with day, month, year properties
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateKey = this.getDateKey(date);
    const color = this.dateColorMap[dateKey];
    
    if (color) {
      return {
        'background-color': color,
        'color': '#ffffff',
        'border-radius': '50%',
        'font-weight': 'bold'
      };
    }
    return {};
  }

  public hasEventsOnDate(dateObj: any): boolean {
    // PrimeNG passes a date object with day, month, year properties
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    return !!this.dateColorMap[this.getDateKey(date)];
  }

  public getDateTooltip(dateObj: any): string | null {
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateKey = this.getDateKey(date);
    const eventTypes = this.dateEventTypeMap[dateKey];
    if (!eventTypes || eventTypes.length === 0) {
      return null;
    }
    return eventTypes.join(", ");
  }

  public onCalendarDateClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onCalendarSelect(event: any): void {
    if (event?.originalEvent) {
      event.originalEvent.preventDefault();
      event.originalEvent.stopPropagation();
    }
    this.selectedDate = new Date(this.lockedCalendarDate);
  }
}

